import * as Dialog from '@radix-ui/react-dialog';
import React, { useEffect, useState } from 'react';
import * as Accordion from '@radix-ui/react-accordion';
import { Type } from '../../base/Type/Type';
import { Box } from '../../base/Box/Box';
import { HeaderStoryblok } from '../../types/storyblok';
import Link from '../../base/Link/Link';
import { Button } from '../../base/Button/Button';
import { RxHamburgerMenu } from 'react-icons/rx';
import { TfiClose } from 'react-icons/tfi';
import classNames from 'classnames';
import Icon from '../../components/Icon/Icon';

// TYPES
export type SmallNavigationProps = Pick<HeaderStoryblok, 'navLinks'>;

// MARKUP
export const SmallNavigation = ({ navLinks }: SmallNavigationProps) => {
  // const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [itemOpen, setItemOpen] = useState('');

  // Ensures menu is closed when resizing to desktop
  useEffect(() => {
    function handleCloseOnResize() {
      const isDesktop = window.matchMedia('(min-width: 480px)').matches;
      if (isDesktop && menuOpen) {
        setMenuOpen(false);
      }
    }

    window.addEventListener('resize', handleCloseOnResize);
    window.addEventListener('orientationchange', handleCloseOnResize);

    return () => {
      window.removeEventListener('resize', handleCloseOnResize);
      window.removeEventListener('orientationchange', handleCloseOnResize);
    };
  }, [menuOpen]);

  // Ensures menu is closed when navigating
  // useEffect(() => {
  //   setMenuOpen(false);
  // }, [location]);

  return (
    <Dialog.Root open={menuOpen} onOpenChange={setMenuOpen}>
      <Dialog.Trigger className="p-0.25 lg:hidden">
        <Type as="span" className="text-h3">
          <RxHamburgerMenu />
        </Type>
      </Dialog.Trigger>

      <Dialog.Overlay className="fixed left-0 top-0 z-10 h-screen w-screen bg-white opacity-70" />

      <Dialog.Content className="absolute right-0 top-0 z-50 h-screen w-full  overflow-y-scroll bg-ffGreen px-1">
        <Box
          as="nav"
          className="mx-auto flex h-full max-w-standard flex-col py-8"
          colourScheme="darkGreen"
          blockSpacing
        >
          <Box
            as="div"
            className="relative flex w-full items-center justify-end pb-8"
          >
            <Dialog.Close className="p-0.25 flex flex-nowrap items-center">
              <Type as="span" className="text-h3">
                <TfiClose />
              </Type>
            </Dialog.Close>
          </Box>
          <Accordion.Root
            type="single"
            collapsible
            className="flex flex-col gap-4"
            value={itemOpen}
            onValueChange={setItemOpen}
          >
            {navLinks?.map((link) => (
              <Accordion.Item
                key={link?._uid}
                value={link?._uid}
                className="mb-5"
              >
                <Accordion.Header className="flex">
                  {!link?.subLinks && (
                    <Link href={link?.link} className="font-helvetica text-h3">
                      {link?.title || link?.linkTitle}
                    </Link>
                  )}
                  {!!link?.subLinks && (
                    <Accordion.Trigger className="flex items-center justify-center">
                      <Type
                        as="span"
                        className="m-0 p-0 font-helvetica text-h3 leading-none"
                      >
                        {link?.title || link?.linkTitle}
                      </Type>
                      <Button
                        type="button"
                        variant={
                          link?._uid === itemOpen
                            ? 'naked-arrow-down'
                            : 'naked-arrow-right'
                        }
                        className="ml-3 !h-12 !w-12"
                      />
                    </Accordion.Trigger>
                  )}
                </Accordion.Header>
                {link?.subLinks && (
                  <Accordion.Content className="overflow-hidden">
                    <Box as="ul" className="flex flex-col gap-3 py-4">
                      {link?.subLinks.map((subLink) => {
                        const isInactiveLink =
                          !subLink?.link?.url && !subLink?.link?.cached_url;

                        return (
                          <Box as="li" key={subLink?._uid} className="py-1">
                            <Link
                              href={subLink?.link}
                              className={classNames(
                                'font-helvetica text-p1 hover:text-ffBlack',
                                isInactiveLink &&
                                  'pointer-events-none opacity-60'
                              )}
                            >
                              {subLink?.title || subLink?.linkTitle}
                            </Link>
                          </Box>
                        );
                      })}
                    </Box>
                  </Accordion.Content>
                )}
              </Accordion.Item>
            ))}
          </Accordion.Root>
          <Box as="div" className="whiteFillSvg -ml-3 flex">
            <Link
              className="[&>svg]:h-12 [&>svg]:w-12"
              href="https://www.linkedin.com/company/foundersfactory/"
            >
              <Icon icon="linkedin" />
            </Link>
            <Link
              className="[&>svg]:h-12 [&>svg]:w-12"
              href="https://twitter.com/foundersfactory"
            >
              <Icon icon="twitter" />
            </Link>
          </Box>
        </Box>
      </Dialog.Content>
    </Dialog.Root>
  );
};
